import { Fragment, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import { Row } from "react-bootstrap";
import { formatCurrency, formatNumber, formatPercent } from "utils/formatNumber";
import { castNumbersInRowData, pathLabelMapping } from "./utils";
import { Badge } from "react-bootstrap";
import { FaArrowDown } from "react-icons/fa";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import ProgressBar from "components/core/basic/ProgressBar";
import { type ConversionPathData, type ConversionPathDataWithAdTypes } from "./types";
import Loading from "components/core/blocks/Loading";
import { Link } from "react-router-dom";
import { type Campaign } from "./PathToPurchaseTable";
import { useAMCReportData } from "../hooks/useAMCReportData";

interface Row {
  original: ConversionPathData;
}

interface ColumnSpec {
  id?: string;
  key?: string;
  compareKey?: string;
}

interface Props {
  row: ConversionPathData;
  manualCampaignLookup: { [key: string]: Campaign };
  autoCampaignLookup: { [key: string]: Campaign };
  audienceCampaignLookup: { [key: string]: Campaign };
  contextualCampaignLookup: { [key: string]: Campaign };
  isAdDataLoading: boolean;
  campaignReportId?: number;
}

const PathToPurchaseExpander = ({
  row,
  manualCampaignLookup,
  autoCampaignLookup,
  audienceCampaignLookup,
  contextualCampaignLookup,
  isAdDataLoading,
  campaignReportId,
}: Props) => {
  const TABLE_ID = `CAMPAIGN_PATH_EXPANDER_TABLE`;

  const filterProps = useCustomTableFilters(TABLE_ID);
  const tableRef = useRef(null);

  const { data: campaignData = [], isLoading } = useAMCReportData(campaignReportId);

  const path = row.path.match(/[A-Z]+-[A-Za-z]+/g)?.map((touchpoint: string) => {
    const key = touchpoint.split("-")[0];

    return pathLabelMapping[key as keyof object]["label"];
  });

  const tableData = useMemo(() => {
    const filteredCampaignData = campaignData
      .filter((row: ConversionPathData) => row.path.length)
      .map((row: ConversionPathData) => {
        if (row.sales_amount) {
          const acos = row.total_cost / row.sales_amount;
          row.acos = acos;
        }
        const adTypePath = row.path
          .slice(1, -1)
          .replace(/\[(\d+),\s/g, "[$1: ")
          .split(",")
          .map((campaign_name: string) => {
            const campaignPrefix = campaign_name.split(": ")[1]?.split(":")[0] ?? "";

            if (campaignPrefix.includes("SD")) {
              return "Sponsored Display";
            }
            if (campaignPrefix.includes("SB")) {
              return "Sponsored Brand";
            }
            if (campaignPrefix.includes("DSP")) {
              return "DSP";
            }
            if (campaignPrefix.includes("SP")) {
              return "Sponsored Product";
            }
            if (campaignPrefix.includes("ST")) {
              return "Streaming TV";
            }

            return "Unknown Ad Type";
          });

        return {
          adTypePath: adTypePath.filter((value: string, index: number) => {
            return index === 0 || value !== adTypePath[index - 1];
          }),
          ...castNumbersInRowData(row),
        };
      })
      .filter((row: ConversionPathDataWithAdTypes) => {
        return (
          row.adTypePath.length === path?.length &&
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          row.adTypePath.every((value: string, index: number) => value === path[index])
        );
      });
    return filteredCampaignData;
  }, [path, campaignData]);

  const metricLimits = useMemo(() => {
    return tableData.reduce((acc: ConversionPathData, row: ConversionPathData) => {
      for (const key in row) {
        const value = row[key];

        if (typeof value === "number") {
          acc[key] = Math.max((acc[key] as number) || -Infinity, value);
        } else if (typeof value === "string") {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            acc[key] = Math.max((acc[key] as number) || -Infinity, numValue);
          }
        }
      }
      return acc;
    }, {} as ConversionPathData);
  }, [tableData]);

  const columnSpecs: ColumnSpec[] = useMemo(
    () => [
      {
        key: "path_occurrences",
        options: {
          columnTitle: "Path Occurences",
          format: "number",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.path_occurrences) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "impressions",
        options: {
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.impressions) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "cost",
        options: {
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatCurrency(v, "USA")}
                <ProgressBar progress={(v / metricLimits.total_cost) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "users_that_purchased",
        options: {
          columnTitle: "Users That Purchased",
          format: "number",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.users_that_purchased) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "sales_amount",
        options: {
          columnTitle: "Sales Amount",
          format: "currency",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatCurrency(v, "USA")}
                <ProgressBar progress={(v / metricLimits.sales_amount) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "purchases",
        options: {
          columnTitle: "Purchases",
          format: "number",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.purchases) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "acos",
        options: {
          columnTitle: "ACOS",
          format: "percent",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatPercent(v)}
                <ProgressBar progress={v * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "user_purchase_rate",
        options: {
          columnTitle: "User Purchase Rate",
          format: "percent",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatPercent(v, 1)}
                <ProgressBar progress={(v / metricLimits.user_purchase_rate) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_users_that_purchased",
        options: {
          columnTitle: "NTB Users That Purchased",
          format: "number",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {v}
                <ProgressBar progress={(v / metricLimits.ntb_users_that_purchased) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_sales_amount",
        options: {
          columnTitle: "NTB Sales Amount",
          format: "currency",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatCurrency(v, "USA")}
                <ProgressBar progress={v / metricLimits.ntb_sales_amount} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_purchases",
        options: {
          columnTitle: "NTB Purchases",
          format: "number",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.ntb_purchases) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_percentage",
        options: {
          columnTitle: "NTB Percentage",
          format: "percent",
          formatter: (v: number) => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatPercent(v, 1)}
                <ProgressBar progress={(v / metricLimits.ntb_percentage) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
    ],
    [metricLimits],
  );

  return (
    <div className="bg-white">
      {isLoading || isAdDataLoading ?
        <Loading height={"300px"} />
      : <Row className="p-5">
          <p className="fs-6">* Not all campaign paths can be mapped due to insufficient unique users</p>
          <Table
            tableRef={tableRef}
            tableId={TABLE_ID}
            isSelectable={false}
            isExpandable={false}
            titleCol={{
              id: "path",
              Header: "Path",
              accessor: "text",
              style: { width: "450px" },
              width: 450,
              Cell: (row: { original: { path: string } }) => {
                const { path } = row.original;

                const pathArray = path
                  .slice(1, -1)
                  .replace(/\[(\d+),\s/g, "[$1: ")
                  .split(",")
                  .map((campaignName: string) => {
                    return {
                      name: campaignName.slice(1, -1).split(":")[2],
                      type: campaignName.slice(1, -1).split(":")[1]?.trim(),
                    };
                  });

                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      fontWeight: "500",
                      fontSize: "1.5rem",
                      alignItems: "center",
                    }}
                  >
                    {pathArray.map((campaign, index) => {
                      const { name, type } = campaign;
                      const campaignTypeOpacity = pathLabelMapping[type as keyof typeof pathLabelMapping].opacity;

                      let associatedMediaPlan;
                      if (name && autoCampaignLookup[name]) {
                        associatedMediaPlan = { ...autoCampaignLookup[name], type: "Auto" };
                      }

                      if (name && manualCampaignLookup[name]) {
                        associatedMediaPlan = { ...manualCampaignLookup[name], type: "Discovery" };
                      }

                      if (name && contextualCampaignLookup[name]) {
                        associatedMediaPlan = { ...contextualCampaignLookup[name], type: "Contextual" };
                      }

                      if (name && audienceCampaignLookup[name]) {
                        associatedMediaPlan = { ...audienceCampaignLookup[name], type: "Audience" };
                      }

                      return (
                        <Fragment key={index}>
                          {associatedMediaPlan ?
                            <Link to={`/user/advertising/dashboard/ads/${associatedMediaPlan.id}`} target="_blank">
                              <Badge
                                className={`badge bg-trellis-purple fs-6 mb-2 me-2 text-wrap`}
                                style={{ opacity: campaignTypeOpacity, position: "relative", display: "block" }}
                              >
                                {associatedMediaPlan.name} ({associatedMediaPlan.type})
                                {index < pathArray.length - 1 ?
                                  <div
                                    style={{ position: "absolute", left: 0, right: 0, width: "100%", bottom: "-12px" }}
                                  >
                                    <FaArrowDown
                                      style={{
                                        fontSize: "10px",
                                        color: "#333",
                                      }}
                                    />
                                  </div>
                                : null}
                              </Badge>
                            </Link>
                          : <Badge
                              className={`badge bg-trellis-purple fs-6 mb-2 me-2 text-wrap`}
                              style={{ opacity: campaignTypeOpacity, position: "relative", display: "block" }}
                            >
                              {name}
                              {index < pathArray.length - 1 ?
                                <div
                                  style={{ position: "absolute", left: 0, right: 0, width: "100%", bottom: "-12px" }}
                                >
                                  <FaArrowDown
                                    style={{
                                      fontSize: "10px",
                                      color: "#333",
                                    }}
                                  />
                                </div>
                              : null}
                            </Badge>
                          }
                        </Fragment>
                      );
                    })}
                  </div>
                );
              },
            }}
            data={tableData}
            columnSpecs={columnSpecs}
            excludedFilterColumns={["conversion_type"]}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            getSearchCriteria={(row: any) => {
              const filterCriteria = row.path;
              return filterCriteria;
            }}
            tableRowDescription={"Paths"}
            defaultSorted={[{ id: "impressions", desc: true }]}
            filterWidget={true}
            hideFilterToggleIcon={true}
            handleBulkChanges={() => {}}
            hideSearchFilter={false}
            hidePagination={false}
            {...filterProps}
            minRows={1}
            selectionKey={"path"}
            ExpandedContent={() => null}
          />
        </Row>
      }
    </div>
  );
};

export default PathToPurchaseExpander;
